import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const images = [
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/6uek5qa5_image.png',
      title: 'The Emerald Suite Living Room',
      category: 'Living Spaces'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/26e8bwp9_image.png',
      title: 'Luxury Bathroom with Golden Fixtures',
      category: 'Bathrooms'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_0902ddbc-a622-478c-8995-4613e012561c/artifacts/bi2ck6w2_image.png',
      title: 'Building Exterior',
      category: 'Exterior'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/kudr3mhs_image.png',
      title: 'Lamborghini Rise — Living Lounge',
      category: 'Living Spaces'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/kbtbntbn_image.png',
      title: 'The Outdoor Lounge',
      category: 'Outdoor'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/dugxaqq1_image.png',
      title: 'Private Garden Entrance',
      category: 'Outdoor'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/r2plyw18_image.png',
      title: 'Signature Bedroom',
      category: 'Bedrooms'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/faz853fu_image.png',
      title: 'Welcome Reception',
      category: 'Reception'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/dofypk0e_image.png',
      title: 'Chef\'s Kitchen — Storage Detail',
      category: 'Kitchens'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/7e4nsgo1_image.png',
      title: 'Granite Cooktop & Prep Station',
      category: 'Kitchens'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/tjpbzh2p_image.png',
      title: 'Azraa Luxury Apartments — Front Facade',
      category: 'Exterior'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/sziw0eab_image.png',
      title: 'Marble Spa Bathroom',
      category: 'Bathrooms'
    }
  ];

  const videos = [
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/ummzuy0o_IMG_7504-1.MP4',
      title: 'Suite Walkthrough',
      category: 'Virtual Tours'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/drshfg8j_Gabedos.mp4',
      title: 'Gazebo & Outdoor Lounge Tour',
      category: 'Virtual Tours'
    }
  ];

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div data-testid="gallery-page" className="min-h-screen bg-[#FAF8F5] pt-24">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="gallery-page-title"
          className="font-serif font-bold tracking-tighter leading-none text-[#1E3A8A] mb-6"
          style={{ fontSize: '26px' }}
        >
          Gallery
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="gallery-page-subtitle"
          className="text-base leading-relaxed text-[#3A3A3A] max-w-3xl mx-auto"
        >
          Experience the elegance and luxury of De Azraa through our curated collection.
        </motion.p>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`gallery-item-${index}`}
              className="relative h-80 overflow-hidden group cursor-pointer"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-end p-6">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-xs tracking-[0.2em] uppercase mb-1">{image.category}</p>
                  <p className="text-white font-serif text-xl">{image.title}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Video Section */}
        {videos.length > 0 && (
          <div className="mt-16">
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#1E3A8A] mb-8 text-center" style={{ fontSize: '26px' }}>
              Virtual Tours
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {videos.map((video, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  data-testid={`gallery-video-${index}`}
                  className="relative overflow-hidden group"
                >
                  <video
                    controls
                    className="w-full h-auto"
                    preload="metadata"
                  >
                    <source src={video.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="mt-3">
                    <p className="text-[#3A3A3A] text-xs tracking-[0.2em] uppercase mb-1">{video.category}</p>
                    <p className="text-[#1A1A1A] font-serif text-xl">{video.title}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              data-testid="lightbox-close-btn"
              className="absolute top-6 right-6 text-white hover:text-[#C9A84C] transition-colors"
            >
              <X size={32} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              data-testid="lightbox-prev-btn"
              className="absolute left-6 text-white hover:text-[#C9A84C] transition-colors text-4xl"
            >
              ‹
            </button>

            <div className="max-w-6xl max-h-[90vh] px-12" onClick={(e) => e.stopPropagation()}>
              <img
                src={images[currentImage].url}
                alt={images[currentImage].title}
                data-testid="lightbox-image"
                className="max-w-full max-h-[90vh] object-contain"
              />
              <p className="text-white text-center mt-4" data-testid="lightbox-caption">
                {images[currentImage].title}
              </p>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              data-testid="lightbox-next-btn"
              className="absolute right-6 text-white hover:text-[#C9A84C] transition-colors text-4xl"
            >
              ›
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;